import { create } from 'zustand';
import { apiClient, type ResumeContext } from '@/lib/api';
import type { CharacterPermanent } from '@danda/shared';

interface PartyMemberSummary {
  id: string;
  name: string;
  avatarEmoji?: string;
  avatarUrl?: string;
  class?: string;
  level?: number;
}

interface PartyData {
  id: string;
  name: string;
  memberCharacterIds: string[];
  members?: PartyMemberSummary[];
  leaderCharacterId?: string;
  selectedModuleId?: string;
  selectedModuleName?: string;
  location?: string;
  status?: string;
  lastSessionEnd?: {
    finalGameTimeSeconds?: number;
  };
  partyImageUrl?: string;
}

interface DashboardState {
  character: CharacterPermanent | null;
  party: PartyData | null;
  activeSessionId: string | null;
  /** Cliffhanger resume-card context (live vs ended-incomplete + decoration). */
  resumeContext: ResumeContext | null;
  loading: boolean;
  error: string | null;
  /**
   * Set to true once `fetch()` has resolved at least once (success OR error).
   * Distinguishes "we haven't loaded yet" from "loaded, character is null".
   * The dashboard's empty-state ("No Character Yet") is gated on this so a
   * remount with stale `loading: false` from a prior route doesn't briefly
   * flash the empty state before the post-mount `fetch()` flips loading
   * back to true.
   */
  hasFetched: boolean;
  fetch: () => Promise<void>;
  /**
   * Re-fetch ONLY the party (no loading flash, no char/session round-trips).
   * Used when a lobby event signals a party-membership change (e.g. a player
   * returning from a training duel) so the dashboard party card drops a
   * now-deleted temp party without a full dashboard reload.
   */
  refreshParty: () => Promise<void>;
  reset: () => void;
}

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

export const useDashboardStore = create<DashboardState>()((set) => ({
  character: null,
  party: null,
  activeSessionId: null,
  resumeContext: null,
  loading: true,
  error: null,
  hasFetched: false,

  fetch: async () => {
    set({ loading: true, error: null });

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const [charResult, sessionResult, partyResult] = await Promise.allSettled([
          apiClient.getMyCharacter(),
          // The resume card is decoration, never a gate: any failure
          // (including a backend without the endpoint yet) degrades to
          // "nothing to resume" instead of failing the whole dashboard.
          apiClient.getResumeContext().catch((): ResumeContext => ({ state: 'none' })),
          apiClient.getParty(),
        ]);

        const results = [charResult, sessionResult, partyResult];
        const anyFailed = results.some((r) => r.status === 'rejected');

        if (anyFailed) {
          const failedEndpoints = [
            charResult.status === 'rejected' ? 'character' : null,
            sessionResult.status === 'rejected' ? 'session' : null,
            partyResult.status === 'rejected' ? 'party' : null,
          ].filter(Boolean);

          if (attempt < MAX_RETRIES) {
            console.warn(
              `[DashboardStore] API failure (attempt ${attempt}/${MAX_RETRIES}), retrying in ${RETRY_DELAY_MS}ms — could not load: ${failedEndpoints.join(', ')}`
            );
            await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
            continue;
          }

          console.error(
            `[DashboardStore] API failure — could not load: ${failedEndpoints.join(', ')}`
          );
          set({
            loading: false,
            hasFetched: true,
            error: 'Polymyth is unavailable. Please try again later.',
          });
          return;
        }

        // Safe to access .value — anyFailed check above guarantees all fulfilled
        const character = (charResult as PromiseFulfilledResult<CharacterPermanent | null>).value;
        const resumeContext = (sessionResult as PromiseFulfilledResult<ResumeContext>).value;
        // activeSessionId derives from the resume context: only a LIVE
        // session routes into /play (ended sessions restart via the lobby).
        const activeSessionId =
          resumeContext.state === 'live' ? (resumeContext.activeSessionId ?? null) : null;
        const partyValue = (
          partyResult as PromiseFulfilledResult<{ party: Record<string, unknown> | null }>
        ).value.party;
        const party = partyValue ? (partyValue as unknown as PartyData) : null;

        set({
          character,
          party,
          activeSessionId,
          resumeContext,
          loading: false,
          hasFetched: true,
        });
        return;
      } catch (err) {
        if (attempt < MAX_RETRIES) {
          console.warn(
            `[DashboardStore] fetch error (attempt ${attempt}/${MAX_RETRIES}), retrying...`
          );
          await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
          continue;
        }
        console.error('[DashboardStore] fetch error:', err);
        set({
          loading: false,
          hasFetched: true,
          error: 'Polymyth is unavailable. Please try again later.',
        });
      }
    }
  },

  refreshParty: async () => {
    try {
      const { party: partyValue } = await apiClient.getParty();
      set({ party: partyValue ? (partyValue as unknown as PartyData) : null });
    } catch (err) {
      console.error('[DashboardStore] refreshParty error:', err);
    }
  },

  reset: () =>
    set({
      character: null,
      party: null,
      activeSessionId: null,
      resumeContext: null,
      loading: true,
      error: null,
      hasFetched: false,
    }),
}));
